﻿import { Ignore } from '../decorators/inject-decorator';
import { Kb3dModel } from '../decorators/kb3d-decorator';
import { Token } from '../token';

export interface IKbVectorLike { 
    x: number;
    y: number;
    z: number;
}

@Kb3dModel({
    token: Token.KbVector,
    trackChanges: false,
    create: args => new KbVector(args.x || 0, args.y || 0, args.z || 0),
})
export class KbVector implements IKbVectorLike {
    constructor(@Ignore() readonly x: number, @Ignore() readonly y: number, @Ignore() readonly z: number) {
        if (!isFinite(x) || isNaN(x)) {
            this.x = 0;
            console.error(
                'Error: Non numeric or infinite value detected in x-value of a vector, please check snapcode for mistakes.'
            );
        }
        if (!isFinite(y) || isNaN(y)) {
            this.y = 0;
            console.error(
                'Error: Non numeric or infinite value detected in y-value of a vector, please check snapcode for mistakes.'
            );
        }
        if (!isFinite(z) || isNaN(z)) {
            this.z = 0;
            console.error(
                'Error: Non numeric or infinite value detected in z-value of a vector, please check snapcode for mistakes.'
            );
        }
    }

    add(v: IKbVectorLike): KbVector {
        return new KbVector(this.x + v.x, this.y + v.y, this.z + v.z);
    }
    subtract(v: IKbVectorLike): KbVector {
        return new KbVector(this.x - v.x, this.y - v.y, this.z - v.z);
    }
    multiply(v: IKbVectorLike): KbVector {
        return new KbVector(this.x * v.x, this.y * v.y, this.z * v.z);
    }
    divide(v: { x: number; y: number; z: number }): KbVector {
        const newX = v.x !== 0 ? this.x / v.x : 0;
        const newY = v.y !== 0 ? this.y / v.y : 0;
        const newZ = v.z !== 0 ? this.z / v.z : 0;
        return new KbVector(newX, newY, newZ);
    }
    crossProduct(v: IKbVectorLike) {
        const x = this.y * v.z - this.z * v.y;
        const y = this.z * v.x - this.x * v.z;
        const z = this.x * v.y - this.y * v.x;
        return new KbVector(x, y, z);
    }
    dotProduct(v: IKbVectorLike) {
        return this.x * v.x + this.y * v.y + this.z * v.z;
    }
    length() {
        return Math.sqrt(this.x * this.x + this.y * this.y + this.z * this.z);
    }
    normalize() {
        const l = this.length();
        if (l === 0 || l === 1.0) {
            return this.clone();
        }
        return this.scale(1.0 / l);
    }
    scale(s: number): KbVector {
        return new KbVector(this.x * s, this.y * s, this.z * s);
    }
    equal(v: IKbVectorLike): boolean {
        return v.x == this.x && v.y == this.y && v.z == this.z;
    }
    clone(): KbVector {
        return new KbVector(this.x, this.y, this.z);
    }
    assign(v: { x?: number | null; y?: number | null; z?: number | null }) {
        return new KbVector(
            typeof v.x === 'number' ? v.x : this.x,
            typeof v.y === 'number' ? v.y : this.y,
            typeof v.z === 'number' ? v.z : this.z
        );
    }
    shiftClone(component: 'x' | 'y' | 'z', amount: number) {
        if (component === 'x') {
            return new KbVector(amount, this.y, this.z);
        } else if (component === 'y') {
            return new KbVector(this.x, amount, this.z);
        } else {
            return new KbVector(this.x, this.y, amount);
        }
    }

    toString() {
        return `{ x: ${this.x}, y: ${this.y}, z: ${this.z} }`;
    }
    toObject() {
        return { x: this.x, y: this.y, z: this.z } as IKbVectorLike;
    }
    static Zero() {
        return new KbVector(0, 0, 0);
    }
    static Identity() {
        return new KbVector(1, 1, 1);
    }
    static From(v: IKbVectorLike) {
        return new KbVector(v.x, v.y, v.z);
    }
}
